const DEFAULT_SERVER_TIMEZONE = "America/New_York";

type ServerTimeContextState = {
  serverNow: string | null;
  serverToday: string | null;
  serverTimezone: string;
  serverNowCapturedAt: number | null;
};

type ServerTimeContextInput = {
  serverNow?: string | null;
  serverToday?: string | null;
  serverTimezone?: string | null;
};

let serverTimeContext: ServerTimeContextState = {
  serverNow: null,
  serverToday: null,
  serverTimezone: DEFAULT_SERVER_TIMEZONE,
  serverNowCapturedAt: null,
};

const SERVER_PARTS_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: DEFAULT_SERVER_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const toServerTimezoneFormatter = (timeZone: string) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });

type CalendarDate = {
  year: number;
  month: number;
  day: number;
};

const normalizeServerTimezone = (timeZone?: string | null): string => {
  if (!timeZone) return DEFAULT_SERVER_TIMEZONE;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return timeZone;
  } catch {
    return DEFAULT_SERVER_TIMEZONE;
  }
};

export const setServerTimeContext = (next: ServerTimeContextInput): void => {
  const nextServerNow = next.serverNow ?? serverTimeContext.serverNow;
  serverTimeContext = {
    serverNow: nextServerNow,
    serverToday: next.serverToday ?? serverTimeContext.serverToday,
    serverTimezone: normalizeServerTimezone(next.serverTimezone ?? serverTimeContext.serverTimezone),
    serverNowCapturedAt:
      next.serverNow !== undefined ? (nextServerNow ? Date.now() : null) : serverTimeContext.serverNowCapturedAt,
  };
};

export const setServerTimezone = (timeZone?: string | null): void => {
  serverTimeContext.serverTimezone = normalizeServerTimezone(timeZone);
};

export const getServerTimezone = (): string => serverTimeContext.serverTimezone;

export const getServerNow = (): string | null => serverTimeContext.serverNow;

export const getServerToday = (): string | null => serverTimeContext.serverToday;

const extractParts = (date: Date, timeZone: string): Record<string, string> => {
  const formatter =
    timeZone === DEFAULT_SERVER_TIMEZONE
      ? SERVER_PARTS_FORMATTER
      : toServerTimezoneFormatter(timeZone);

  return formatter.formatToParts(date).reduce<Record<string, string>>((acc, part) => {
    if (part.type !== "literal") {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});
};

const getReferenceInstant = (): Date => {
  const serverNow = getServerNow();
  if (serverNow) {
    const parsed = new Date(serverNow);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return new Date();
};

export const getCurrentServerInstant = (): Date => {
  const serverNow = getServerNow();
  const serverNowCapturedAt = serverTimeContext.serverNowCapturedAt;

  if (serverNow && serverNowCapturedAt) {
    const parsed = new Date(serverNow);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(parsed.getTime() + (Date.now() - serverNowCapturedAt));
    }
  }

  return new Date();
};

export const getServerCalendarDate = (): CalendarDate => {
  const knownToday = getServerToday();
  if (knownToday) {
    const match = knownToday.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
      };
    }
  }

  const parts = extractParts(getReferenceInstant(), getServerTimezone());
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
  };
};

const toUtcNoon = ({ year, month, day }: CalendarDate): Date =>
  new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

const fromUtcDate = (date: Date): CalendarDate => ({
  year: date.getUTCFullYear(),
  month: date.getUTCMonth() + 1,
  day: date.getUTCDate(),
});

export const addServerCalendarDays = (date: CalendarDate, days: number): CalendarDate => {
  const utcDate = toUtcNoon(date);
  utcDate.setUTCDate(utcDate.getUTCDate() + days);
  return fromUtcDate(utcDate);
};

export const getServerWeekStart = (date: CalendarDate): CalendarDate => {
  const utcDate = toUtcNoon(date);
  const dayOfWeek = utcDate.getUTCDay();
  const daysFromMonday = (dayOfWeek + 6) % 7;
  return addServerCalendarDays(date, -daysFromMonday);
};

export const getServerWeekEnd = (date: CalendarDate): CalendarDate =>
  addServerCalendarDays(getServerWeekStart(date), 6);

const pad = (value: number): string => String(value).padStart(2, "0");

export const formatServerDateTime = (
  date: CalendarDate,
  hours: number,
  minutes: number,
  seconds: number,
): string =>
  `${date.year}-${pad(date.month)}-${pad(date.day)}T${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

export const formatServerDate = (date: CalendarDate): string =>
  `${date.year}-${pad(date.month)}-${pad(date.day)}`;

/**
 * Maps a YYYY-MM-DD date string to the legacy `q.range_default_enum` value
 * used by RunDetails#readScheduleFiltersFromUrl to pre-filter the schedule tab.
 *
 * Returns:
 *   "today"     — dateStr matches the current server date
 *   "yesterday" — dateStr matches yesterday
 *   "tomorrow"  — dateStr matches tomorrow
 *   "time"      — any other date (caller must also supply q.range_default_date)
 */
export const resolveRunScheduleTimePeriod = (dateStr: string): string => {
  const today     = formatServerDate(getServerCalendarDate());
  const tomorrow  = formatServerDate(addServerCalendarDays(getServerCalendarDate(), 1));
  const yesterday = formatServerDate(addServerCalendarDays(getServerCalendarDate(), -1));

  if (dateStr === today)     return 'today';
  if (dateStr === tomorrow)  return 'tomorrow';
  if (dateStr === yesterday) return 'yesterday';
  return 'time';
};

export const formatServerStartOfDay = (date: CalendarDate): string =>
  formatServerDateTime(date, 0, 0, 0);

export const formatServerEndOfDay = (date: CalendarDate): string =>
  formatServerDateTime(date, 23, 59, 59);
